<template>
  <a-page
    id="hotel-management-app"
    :image_source="image_source"
    :image_height="image_height"
  >
    <!-- PAGE HEADER -->
    <!-- EDIT HOTEL MESSAGE -->
    <div
      v-if="show_editing"
      class="d-flex justify-center align-center text-center white--text mb-6"
      style="position:relative"
    >
      <div class="px-10 py-12">
        <span class="text-h5 d-block">{{ $t("modifying_hotel") }}</span>
      </div>
    </div>
    <!-- CREATE HOTEL MESSAGE -->
    <div
      v-if="show_creating"
      class="d-flex justify-center align-center text-center white--text mb-6"
      style="position:relative"
    >
      <div class="px-10 py-12">
        <span class="text-h5 d-block">{{ $t("creating_new_hotel") }}</span>
      </div>
    </div>
    <!-- KPIS -->
    <div
      v-if="!show_editing && !show_creating"
      class="d-flex justify-center align-center text-center white--text mb-6"
      style="position:relative"
    >
      <div class="px-10">
        <span class="text-h1 d-block">{{ hotels_count }}</span>
        <span class="text-h5 d-block">{{ $t("hotels") }}</span>
      </div>
      <div class="px-10">
        <span class="text-h1 d-block">{{ active_hotels_count }}</span>
        <span class="text-h5 d-block">{{ $t("actives") }}</span>
      </div>
    </div>
    <!-- end PAGE HEADER -->

    <!-- LIST -->
    <v-card v-if="!show_editing && !show_creating" class="mb-5">
      <v-app-bar flat color="white">
        <v-toolbar-title class="title font-weight-bold">
          {{ $t("hotels") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          v-if="can_create_hotel"
          rounded
          color="secondary"
          class="mr-4"
          @click="showCreateHotel"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t("add_hotel") }}
        </v-btn>
        <v-text-field
          v-model="search"
          ref="search_hotels"
          block
          rounded
          outlined
          dense
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('search_hotel')"
          @click:clear="search = ''"
          hide-details
          style="max-width:300px"
        ></v-text-field>
      </v-app-bar>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="filtered_hotels"
          :item-class="getDatatableRowClass"
          :no-data-text="$t('no_hotels_found')"
          hide-default-footer
          disable-pagination
          class="elevation-0"
          @click:row="showModifyHotel"
        >
          <template v-slot:[`item.name`]="{ item }">
            <span :class="`ml-${getLevels(item) * 5}`"></span>
            <v-icon
              v-if="parseInt(item.is_group) === 1"
              size="16"
              class="mr-1"
              color="accent"
            >
              mdi-home-city
            </v-icon>
            <v-icon v-else size="16" class="mr-1" color="primary">
              mdi-office-building
            </v-icon>
            <!-- <span class="grey--text caption">
              {{ fathersName(item.father_id) }}
            </span> -->
            {{ item.name }}
          </template>
          <template v-slot:[`item.subscription_plan`]="{ item }">
            <template v-if="parseInt(item.is_group) === 0">
              <v-chip
                v-if="item.settings && item.settings.subscription_plan"
                small
                :color="planColor(item.settings.subscription_plan)"
                >{{ planLabel(item.settings.subscription_plan) }}</v-chip
              >
            </template>
          </template>
          <template v-slot:[`item.revenue_mode`]="{ item }">
            <template v-if="parseInt(item.is_group) === 0">
              <span
                v-if="item.settings && item.settings.revenue_mode"
                class="caption"
              >
                {{ productionModeLabel(item.settings.revenue_mode) }}
              </span>
            </template>
          </template>
          <template v-slot:[`item.pms`]="{ item }">
            <template v-if="parseInt(item.is_group) === 0">
              {{ item.audit.pms }}
            </template>
          </template>
          <template v-slot:[`item.channel`]="{ item }">
            <template v-if="parseInt(item.is_group) === 0">
              {{ item.audit.channel_manager }}
            </template>
          </template>
          <template v-slot:[`item.rate`]="{ item }">
            <template v-if="parseInt(item.is_group) === 0">
              <v-icon v-if="item.audit.rate" color="green">
                mdi-check-circle-outline
              </v-icon>
              <v-icon v-else color="red">
                mdi-close-circle-outline
              </v-icon>
              <span class="ml-1" v-if="item.audit.rate_update !== '-'">{{
                item.audit.rate_update
              }}</span>
            </template>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <template v-if="parseInt(item.active)">
              <v-chip small color="secondary">{{ $t("active") }}</v-chip>
            </template>
            <template v-else>
              <v-chip small>{{ $t("unactive") }}</v-chip>
            </template>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip
              v-if="
                can_create_hotel &&
                  item.audit &&
                  item.audit.alerts &&
                  item.audit.alerts.length > 0
              "
              left
              max-width="320"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click.stop>
                  <v-icon small color="warning">mdi-alert-outline</v-icon>
                </v-btn>
              </template>
              <div>
                <div
                  v-for="(alert, i) in item.audit.alerts"
                  :key="i"
                  class="caption"
                  :class="{ 'mt-1': i > 0 }"
                >
                  <v-icon x-small color="warning" class="mr-1"
                    >mdi-alert-outline</v-icon
                  >
                  {{ alert }}
                </div>
              </div>
            </v-tooltip>
            <v-btn
              @click.stop="deactivateHotel(item.id)"
              v-if="
                can_create_hotel &&
                  parseInt(item.active) &&
                  parseInt(item.id) !== parseInt(current_hotel.id)
              "
              icon
              color="secondary"
              ><v-icon small>mdi-eye-off-outline</v-icon></v-btn
            >
            <v-btn
              @click.stop="activateHotel(item.id)"
              v-if="
                can_create_hotel &&
                  !parseInt(item.active) &&
                  parseInt(item.id) !== parseInt(current_hotel.id)
              "
              icon
              color="secondary"
              ><v-icon small>mdi-eye-outline</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- CREATE HOTEL FORM -->
    <v-card v-if="show_creating" class="mb-5">
      <v-app-bar flat color="white">
        <v-toolbar-title class="title font-weight-light">
          <v-btn icon fab @click="hideCreateHotel" class="mb-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="font-weight-bold">{{ $t("new_hotel_form_title") }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          rounded
          color="secondary"
          :disabled="!valid_create"
          @click="createHotel"
          :loading="creating"
        >
          {{ $t("save") }}
        </v-btn>
      </v-app-bar>
      <v-card-text class="pt-0 pl-8">
        <v-form v-model="valid_create">
          <v-row>
            <v-col cols="12">
              <v-card class="elevation-0" outlined style="height: 100%;">
                <v-card-title>
                  {{ $t("settings_compset_booking_url") }}
                </v-card-title>
                <v-card-text class="pb-0">
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <a-booking-url-field
                        v-model="new_hotel.booking_url"
                        :label="$t('hotel_booking_url')"
                        hint="https://www.booking.com/hotel/{iso}/{nombre-hotel}.{iso}.html"
                        :clean-https="false"
                        outlined
                        rounded
                        dense
                      ></a-booking-url-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card class="elevation-0" outlined style="height: 100%;">
                <v-card-title>
                  {{ $t("compset_urls") }}
                </v-card-title>
                <v-card-text class="pb-0">
                  <v-row
                    v-for="(url, index) in new_hotel.compset_urls"
                    :key="'compset-' + index"
                  >
                    <v-col cols="6" class="py-0 d-flex align-start">
                      <a-booking-url-field
                        v-model="new_hotel.compset_urls[index]"
                        :label="$t('hotel_booking_url')"
                        hint="https://www.booking.com/hotel/{iso}/{nombre-hotel}.{iso}.html"
                        :clean-https="false"
                        outlined
                        rounded
                        dense
                        @paste="onPasteCompsetUrls"
                        class="flex-grow-1"
                      ></a-booking-url-field>
                      <v-btn icon class="ml-2" @click="removeCompsetUrl(index)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <div class="d-flex justify-start mb-4">
                    <v-btn icon @click="addCompsetUrl">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card class="elevation-0" outlined style="height: 100%;">
                <v-card-title>
                  {{ $t("configuration") }}
                </v-card-title>
                <v-card-text class="pb-0">
                  <v-row>
                    <v-col cols="3" class="py-0">
                      <v-select
                        v-model="new_hotel.production_mode"
                        :items="production_mode_options"
                        :label="$t('production_mode')"
                        :rules="[v => !!v || $t('required')]"
                        outlined
                        rounded
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="3" class="py-0">
                      <v-select
                        v-model="new_hotel.includes_board"
                        :items="includes_board_options"
                        :label="$t('room_revenue')"
                        :rules="[
                          v => (v !== null && v !== undefined) || $t('required')
                        ]"
                        outlined
                        rounded
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>
                  <a-hotel-pms-config
                    :item="new_hotel"
                    :required="true"
                    :can-manage-pms="true"
                    @pms-change="resetPmsFields"
                  ></a-hotel-pms-config>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- RESET REVENUE CONFIRMATION DIALOG -->
    <v-dialog v-model="show_confirm_reset_revenue" max-width="520" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          {{ $t("confirm_reset_revenue_title") }}
        </v-card-title>
        <v-card-text>
          <v-alert type="error" outlined class="mb-2">
            <span
              v-html="
                $t('confirm_reset_revenue_text', { name: item_to_edit.name })
              "
            ></span>
          </v-alert>
          <p class="body-2 mt-2">{{ $t("confirm_reset_revenue_confirm") }}</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn
            color="error"
            rounded
            depressed
            class="px-5"
            @click="confirmAndSetHotel"
          >
            {{ $t("yes_reset_and_save") }}
          </v-btn>
          <v-btn
            text
            rounded
            depressed
            class="px-5"
            @click="show_confirm_reset_revenue = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT HOTEL FORM -->
    <v-card v-if="show_editing" class="mb-5">
      <v-app-bar flat color="white">
        <v-toolbar-title class="title font-weight-light">
          <v-btn icon fab @click="hideModifyHotel" class="mb-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span>{{ $t("you_are_editing") }}</span>
          <span class="font-weight-bold ml-2">{{ item_to_edit.name }}</span>
          <span v-if="parseInt(item_to_edit.is_group) === 1" class="ml-2">
            {{ "(" + $t("group") + ")" }}
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          rounded
          color="secondary"
          :disabled="!valid_edit"
          @click="setHotel"
          :loading="saving"
          >{{ $t("save") }}</v-btn
        >
      </v-app-bar>
      <v-card-text class="pt-0 pl-16">
        <v-form v-model="valid_edit">
          <v-row>
            <v-col cols="12" md="12">
              <!-- <v-card-title class="pl-0">{{ $t("general") }}</v-card-title> -->
              <v-row>
                <v-col cols="4">
                  <v-text-field
                    v-model="item_to_edit.name"
                    maxlength="50"
                    counter
                    :rules="name_rules"
                    :label="$t('name')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-card class="mt-4">
                <v-tabs v-model="tab_config" rounded>
                  <v-tabs-slider></v-tabs-slider>
                  <v-tab
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rms_hotelsdot
                    "
                  >
                    {{ $t("audit") }}
                  </v-tab>
                  <v-tab
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        (item_to_edit.permissions.rms ||
                          item_to_edit.permissions.rms_hotelsdot ||
                          item_to_edit.permissions.rms_axel ||
                          item_to_edit.permissions.rms_clicktotravel)
                    "
                  >
                    {{ $t("configuration") }}
                  </v-tab>
                  <v-tab
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rate
                    "
                  >
                    {{ $t("compset") }}
                  </v-tab>
                  <v-tab>
                    {{ $t("visibilities") }}
                  </v-tab>
                  <v-tab
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rms_hotelsdot &&
                        Object.keys(item_to_edit.audit.data_validation).length >
                          0
                    "
                  >
                    {{ $t("data_validation") }}
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab_config" class="px-4">
                  <v-tab-item
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rms_hotelsdot
                    "
                  >
                    <a-hotel-audit :item="item_to_edit"></a-hotel-audit>
                  </v-tab-item>
                  <v-tab-item
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        (item_to_edit.permissions.rms ||
                          item_to_edit.permissions.rms_hotelsdot ||
                          item_to_edit.permissions.rms_axel ||
                          item_to_edit.permissions.rms_clicktotravel)
                    "
                  >
                    <a-hotel-configuration
                      :item="item_to_edit"
                      :original-pms="original_pms"
                      :reset-revenue-data.sync="reset_revenue_data"
                      :can-manage-pms="can_create_hotel"
                    ></a-hotel-configuration>
                  </v-tab-item>
                  <v-tab-item
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rate
                    "
                  >
                    <a-hotel-compset :item="item_to_edit"></a-hotel-compset>
                  </v-tab-item>
                  <v-tab-item>
                    <permissions-editor
                      :item="item_to_edit"
                    ></permissions-editor>
                  </v-tab-item>
                  <v-tab-item
                    v-if="
                      parseInt(item_to_edit.is_group) === 0 &&
                        item_to_edit.permissions.rms_hotelsdot &&
                        Object.keys(item_to_edit.audit.data_validation).length >
                          0
                    "
                  >
                    <a-hotel-data-validation
                      :item="item_to_edit"
                    ></a-hotel-data-validation>
                  </v-tab-item>
                </v-tabs-items>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </a-page>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import aPage from "@/components/aPage";
import PermissionsEditor from "@/components/permissions/PermissionsEditor";
import aHotelCompset from "@/components/settings/aHotelCompset";
import aHotelAudit from "@/components/settings/aHotelAudit";
import aHotelDataValidation from "@/components/settings/aHotelDataValidation";
import aHotelConfiguration from "@/components/settings/aHotelConfiguration";
import aHotelPmsConfig from "@/components/settings/aHotelPmsConfig";
import aBookingUrlField from "@/components/aBookingUrlField";
import * as Utils from "@/utils.js";

export default {
  name: "settings-hotel",
  components: {
    aPage,
    PermissionsEditor,
    aHotelCompset,
    aHotelAudit,
    aHotelDataValidation,
    aHotelConfiguration,
    aHotelPmsConfig,
    aBookingUrlField
  },
  data: () => ({
    image_source: "/img/header-2.jpg",
    image_height: 280,
    item_to_edit: {},
    show_editing: false,
    show_creating: false,
    saving: false,
    creating: false,
    valid_edit: true,
    valid_create: false,
    tab_config: 0,
    original_pms: null,
    reset_revenue_data: false,
    show_confirm_reset_revenue: false,
    search: "",
    new_hotel: {
      booking_url: "",
      compset_urls: [""],
      pms: null,
      production_mode: null,
      includes_board: null,
      settings: {
        subscription_plan: null,
        ulyses_api_environment: "",
        ulyses_api_username: "",
        ulyses_api_password: "",
        ulyses_private_api_environment: "",
        ulyses_authorization_key: "",
        pms_chain_id: "",
        pms_hotel_id: "",
        guestpro_hotel_id: "",
        fns_rooms_user: "",
        fns_rooms_password: "",
        fns_rooms_pms_id: "",
        fns_rooms_alojamiento_id: "",
        fns_rooms_central_id: "",
        fns_rooms_moneda_id: ""
      }
    }
  }),
  methods: {
    getDatatableRowClass() {
      return "a-datatable-row";
    },
    planLabel(plan) {
      const map = {
        discover: this.$t("plan_discover"),
        expand: this.$t("plan_expand"),
        optimise: this.$t("plan_optimise")
      };
      return map[plan] || plan;
    },
    planColor(plan) {
      const map = {
        discover: "accent",
        optimise: "secondary",
        expand: "primary"
      };
      return map[plan] || "grey";
    },
    productionModeLabel(mode) {
      const map = {
        net: this.$t("production_mode_net_option"),
        gross: this.$t("production_mode_gross_option")
      };
      return map[mode] || mode;
    },
    getEmptyNewHotel() {
      return {
        booking_url: "",
        compset_urls: [""],
        pms: null,
        production_mode: null,
        includes_board: null,
        settings: {
          subscription_plan: null,
          ulyses_api_environment: "",
          ulyses_api_username: "",
          ulyses_api_password: "",
          ulyses_private_api_environment: "",
          ulyses_authorization_key: "",
          pms_chain_id: "",
          pms_hotel_id: "",
          guestpro_hotel_id: "",
          fns_rooms_user: "",
          fns_rooms_password: "",
          fns_rooms_pms_id: "",
          fns_rooms_alojamiento_id: "",
          fns_rooms_central_id: "",
          fns_rooms_moneda_id: ""
        }
      };
    },
    resetPmsFields() {
      this.new_hotel.settings.ulyses_api_environment = "";
      this.new_hotel.settings.ulyses_api_username = "";
      this.new_hotel.settings.ulyses_api_password = "";
      this.new_hotel.settings.ulyses_private_api_environment = "";
      this.new_hotel.settings.ulyses_authorization_key = "";
      this.new_hotel.settings.pms_chain_id = "";
      this.new_hotel.settings.pms_hotel_id = "";
      this.new_hotel.settings.guestpro_hotel_id = "";
      this.new_hotel.settings.fns_rooms_user = "";
      this.new_hotel.settings.fns_rooms_password = "";
      this.new_hotel.settings.fns_rooms_pms_id = "";
      this.new_hotel.settings.fns_rooms_alojamiento_id = "";
      this.new_hotel.settings.fns_rooms_central_id = "";
      this.new_hotel.settings.fns_rooms_moneda_id = "";
    },
    showCreateHotel() {
      this.show_creating = true;
      this.new_hotel = this.getEmptyNewHotel();
    },
    hideCreateHotel() {
      this.show_creating = false;
      this.new_hotel = this.getEmptyNewHotel();
    },
    addCompsetUrl() {
      this.new_hotel.compset_urls.push("");
    },
    removeCompsetUrl(index) {
      this.new_hotel.compset_urls.splice(index, 1);
    },
    onPasteCompsetUrls(e) {
      let pastedText = undefined;
      if (window.clipboardData && window.clipboardData.getData) {
        pastedText = window.clipboardData.getData("Text");
      } else if (e.clipboardData && e.clipboardData.getData) {
        pastedText = e.clipboardData.getData("text/plain");
      }
      if (pastedText !== undefined) {
        const lines = pastedText.split("\n");
        if (lines.length > 1) {
          e.preventDefault();
          this.new_hotel.compset_urls = [];
          for (let i = 0; i < lines.length; i++) {
            if (lines[i].trim() !== "") {
              this.new_hotel.compset_urls.push(lines[i].trim());
            }
          }
        }
      }
    },
    createHotel() {
      const vm = this;
      this.creating = true;
      vm.$store.dispatch("setSnackbar", {
        title: vm.$t("creating_new_hotel"),
        color: "secondary"
      });
      this.$store
        .dispatch("createHotel", this.new_hotel)
        .then(() => {
          this.show_creating = false;
          this.creating = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("created_hotel"),
            text: this.$t("created_hotel_ok"),
            color: "accent"
          });
          this.hideCreateHotel();
        })
        .catch(() => {
          this.creating = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("something_went_wrong"),
            color: "error"
          });
        });
    },
    showModifyHotel(hotel) {
      this.show_editing = true;
      this.item_to_edit = JSON.parse(JSON.stringify(hotel));
      this.original_pms = hotel.pms || null;
      this.reset_revenue_data = false;
    },
    hideModifyHotel() {
      this.show_editing = false;
      this.item_to_edit = {};
      this.original_pms = null;
      this.reset_revenue_data = false;
    },
    deactivateHotel: function(hotel_id) {
      const vm = this;
      if (this.current_hotel.id === parseInt(hotel_id)) {
        this.$store.dispatch("setSnackbar", {
          title: vm.$t("cannot_deactivate_hotel"),
          color: "error"
        });
        return;
      }

      vm.$store.dispatch("setSnackbar", {
        title: vm.$t("deactivating_hotel"),
        color: "secondary"
      });

      this.$store
        .dispatch("deactivateHotel", hotel_id)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("deactivated_hotel"),
            color: "accent"
          });
          this.item_to_edit = {};
        })
        .catch(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("something_went_wrong"),
            color: "error"
          });
          this.item_to_edit = {};
        });
    },
    activateHotel: function(hotel_id) {
      const vm = this;
      vm.$store.dispatch("setSnackbar", {
        title: vm.$t("activating_hotel"),
        color: "secondary"
      });

      this.$store
        .dispatch("activateHotel", hotel_id)
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("activated_hotel"),
            // text: this.$t("modified_hotel_ok", { name : this.item_to_edit.name }),
            color: "accent"
          });
          this.item_to_edit = {};
        })
        .catch(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("something_went_wrong"),
            color: "error"
          });
          this.item_to_edit = {};
        });
    },
    setHotel: function() {
      if (this.reset_revenue_data && this.pms_changed) {
        this.show_confirm_reset_revenue = true;
        return;
      }
      this.doSetHotel();
    },
    confirmAndSetHotel: function() {
      this.show_confirm_reset_revenue = false;
      this.doSetHotel();
    },
    doSetHotel: function() {
      const vm = this;
      this.saving = true;
      const hotelPayload = Object.assign({}, this.item_to_edit, {
        reset_revenue: this.reset_revenue_data && this.pms_changed
      });
      vm.$store.dispatch("setSnackbar", {
        title: vm.$t("modifying_hotel"),
        color: "secondary"
      });
      this.$store
        .dispatch("updateHotel", hotelPayload)
        .then(() => {
          this.show_editing = false;
          this.saving = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("modified_hotel"),
            text: this.$t("modified_hotel_ok", {
              name: this.item_to_edit.name
            }),
            color: "accent"
          });

          // We update current_hotel if needed
          if (this.current_hotel.id === parseInt(this.item_to_edit.id)) {
            this.$store.dispatch("fetchHotels");
          }

          this.original_pms = null;
          this.reset_revenue_data = false;
          this.item_to_edit = {};
        })
        .catch(() => {
          this.show_editing = false;
          this.saving = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("something_went_wrong"),
            color: "error"
          });
          this.item_to_edit = {};
        });
    },
    fathersName(id) {
      if (id === null) {
        return "";
      }
      const father = this.hotels.find(hotel => hotel.id === id);
      if (father) {
        return this.fathersName(father.father_id) + father.name + " > ";
      }
      return "";
    },
    getLevels(hotel) {
      let levels = [];
      let father = this.hotels.find(h => h.id === hotel.father_id);
      while (father) {
        levels.push(father);
        father = this.hotels.find(h => h.id === father.father_id);
      }
      return levels.length;
    },
    handleKeydown(event) {
      const search = this.$refs.search_hotels;
      const validKey = /^[a-zA-Z0-9]$/;
      if (
        document.activeElement !== search.$el.querySelector("input") &&
        validKey.test(event.key) &&
        !event.metaKey
      ) {
        search.focus();
        this.search += event.key;
        event.preventDefault();
      }
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("name"),
          align: "start",
          sortable: true,
          value: "name"
        },
        {
          text: this.$t("plan"),
          align: "center",
          sortable: true,
          value: "subscription_plan"
        },
        {
          text: this.$t("production_mode"),
          align: "center",
          sortable: true,
          value: "revenue_mode"
        },
        {
          text: "PMS",
          align: "center",
          sortable: true,
          value: "pms"
        },
        {
          text: this.$t("channel"),
          align: "center",
          sortable: true,
          value: "channel"
        },
        {
          text: this.$t("rate"),
          align: "center",
          sortable: true,
          value: "rate"
        },
        {
          text: this.$t("status"),
          align: "center",
          sortable: true,
          value: "status"
        },
        {
          text: null,
          align: "center",
          sortable: false,
          value: "actions"
        }
      ];
    },
    filtered_hotels() {
      let hotels = [];
      const includedIds = new Set();
      let fathers = this.hotels.filter(h => parseInt(h.is_group) === 1);
      fathers = fathers.sort(
        (a, b) => parseInt(a.father_id) - parseInt(b.father_id)
      );
      fathers.forEach(father => {
        hotels.push(father);
        includedIds.add(String(father.id));
        this.hotels
          .filter(h => {
            return (
              parseInt(h.father_id) === parseInt(father.id) &&
              parseInt(h.is_group) === 0
            );
          })
          .forEach(child => {
            hotels.push(child);
            includedIds.add(String(child.id));
          });
      });
      // Hotels whose parent group is not in the scoped list (e.g. root grouping filtered out)
      this.hotels
        .filter(h => !includedIds.has(String(h.id)))
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(hotel => {
          hotels.push(hotel);
          includedIds.add(String(hotel.id));
        });

      let hotels_visible = [];
      let filtered_hotels = hotels;

      if (this.search !== "" && this.search !== null) {
        filtered_hotels = [];
        const search = this.search.toLowerCase();

        hotels.forEach(hotel => {
          const match =
            hotel.name.toLowerCase().includes(search) ||
            hotel.audit.city.toLowerCase().includes(search) ||
            hotel.audit.pms.toLowerCase().includes(search) ||
            hotel.audit.channel_manager.toLowerCase().includes(search);
          hotels_visible[hotel.id] = match;
        });

        hotels.forEach(hotel => {
          if (hotels_visible[hotel.id]) {
            // Mirar si es grupo entonces hacer visibles todos los hijos
            if (parseInt(hotel.is_group) === 1) {
              hotels.forEach(h => {
                if (parseInt(h.father_id) === parseInt(hotel.id)) {
                  hotels_visible[h.id] = true;
                }
              });
            } else {
              // Si no es grupo, hacer visible el padre
              hotels_visible[hotel.father_id] = true;
            }
          }
        });

        filtered_hotels = hotels.filter(hotel => hotels_visible[hotel.id]);
      }

      return filtered_hotels;
    },
    active_hotels() {
      return this.hotels.filter(hotel => parseInt(hotel.active));
    },
    hotels_count() {
      return this.hotels.length;
    },
    active_hotels_count() {
      return this.active_hotels.length;
    },
    can_create_hotel() {
      return (
        this.current_user &&
        this.current_user.permissions &&
        this.current_user.permissions.hotelmanagement_create
      );
    },
    pms_changed() {
      return (
        this.original_pms !== null &&
        this.item_to_edit &&
        this.item_to_edit.pms !== undefined &&
        this.item_to_edit.pms !== this.original_pms
      );
    },
    production_mode_options() {
      return [
        { text: this.$t("production_mode_net_option"), value: "net" },
        { text: this.$t("production_mode_gross_option"), value: "gross" }
      ];
    },
    includes_board_options() {
      return [
        { text: this.$t("only_accommodation"), value: false },
        { text: this.$t("accommodation_and_board"), value: true }
      ];
    },
    ...mapState({
      hotels: state => state.hotel.all_hotels,
      hotel: state => state.hotel.hotel,
      new_hotel_model: state => state.hotel.new_hotel_model,
      available_permissions: state => state.hotel.available_permissions,
      snackbar: state => state.snackbar,
      show_hotel_list: state => state.hotel.show_hotel_list
    }),
    ...mapGetters({
      name_rules: "name_rules",
      current_hotel: "current_hotel",
      current_user: "current_user"
    })
  },
  watch: {
    show_editing: {
      handler: function(val) {
        if (!val && !this.show_creating) {
          window.addEventListener("keydown", this.handleKeydown);
        } else {
          window.removeEventListener("keydown", this.handleKeydown);
        }
      },
      immediate: true
    },
    show_creating: {
      handler: function(val) {
        if (!val && !this.show_editing) {
          window.addEventListener("keydown", this.handleKeydown);
        } else {
          window.removeEventListener("keydown", this.handleKeydown);
        }
      }
    },
    show_hotel_list: {
      handler: function(val) {
        if (!val) {
          window.addEventListener("keydown", this.handleKeydown);
        } else {
          window.removeEventListener("keydown", this.handleKeydown);
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$store.dispatch("fetchAllHotels").catch(() => {
      Utils.showError(this, "something_went_wrong");
    });
  }
};
</script>
<style></style>
